import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ImageOffIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { formatPrice, Product, productDetailRoute } from '../data/products';
import { SiteLink } from './SiteLink';
type ProductCardProps = {
  product: Product;
  onAddToCart: (quantity: number) => void;
};
export function ProductCard({
  product,
  onAddToCart
}: ProductCardProps) {
  const [quantity, setQuantity] = useState(1);
  const quantityId = `qty-${product.sku}`;
  const isOutOfStock = product.availability.toLowerCase().includes('out of stock');
  return <article className="flex flex-col overflow-hidden rounded-[6px] border border-ink/15 bg-white" data-id="element-307" data-mp="td9y0wopi5mm">
      <Link to={productDetailRoute} tabIndex={-1} aria-hidden="true" className="block aspect-square w-full overflow-hidden bg-white p-3" data-id="element-308" data-mp="yce59hs2ltab">
        {product.image ? <img src={product.image} alt={product.alt} className="h-full w-full object-contain" loading="lazy" decoding="async" data-id="element-309" data-mp="303hhuclithxi" /> : <span className="flex h-full w-full flex-col items-center justify-center gap-2 bg-water/70 px-3 text-center" data-id="element-310" data-mp="3gzlwrcigweq1">
            <ImageOffIcon className="h-7 w-7 text-ink/40" aria-hidden="true" data-id="element-311" data-mp="3czlgdmbt5tgc" />
            <span className="text-[13.5px] leading-5 text-ink/60" data-id="element-312" data-mp="37zz3xf3t0h8l">
              Product image not available.
            </span>
          </span>}
      </Link>

      <div className="flex flex-1 flex-col border-t border-ink/10 p-4" data-id="element-313" data-mp="8hzjqb78lzep">
        <h3 className="text-[16.5px] font-semibold leading-6 text-ink" data-id="element-314" data-mp="4jib1n864gv6">
          <SiteLink to={productDetailRoute} className="transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-315" data-mp="3vnyx0lol8jqc">
            {product.name}
          </SiteLink>
        </h3>
        <p className="mt-1 text-[14.5px] text-ink/60" data-id="element-316" data-mp="2kbfz59jyqml9">SKU {product.sku}</p>

        <p className="mt-2 text-[19px] font-semibold text-ink" data-id="element-317" data-mp="2faie8p7kjeg2">{formatPrice(product.price)}</p>
        <p className={`mt-1 text-[14.5px] font-medium ${isOutOfStock ? 'text-ink/60' : 'text-pine'}`} data-id="element-318" data-mp="2grpaaazd8wyi">
          {product.availability}
        </p>

        <div className="mt-auto pt-4" data-id="element-319" data-mp="27rshpy9uvdy6">
          {isOutOfStock ? <div className="h-11" aria-hidden="true" data-id="element-320" data-mp="27tlpxi04a68a" /> : <div className="flex items-center gap-2" data-id="element-321" data-mp="2cosns96pggpa">
            <label htmlFor={quantityId} className="text-[14.5px] font-medium text-ink/75" data-id="element-322" data-mp="1y0xsqm586091">
              Qty
            </label>
            <div className="flex items-center rounded-btn border border-ink/25" data-id="element-323" data-mp="22sxzaxfsvrel">
              <button type="button" aria-label={`Decrease quantity of ${product.name}`} onClick={() => setQuantity(value => Math.max(1, value - 1))} className="flex h-11 w-11 items-center justify-center text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-324" data-mp="1p1kihr9a17n9">
                <MinusIcon className="h-4 w-4" aria-hidden="true" data-id="element-325" data-mp="1k3ren4vg4pq5" />
              </button>
              <input id={quantityId} type="number" min={1} inputMode="numeric" value={quantity} onChange={event => {
              const next = Number(event.target.value);
              setQuantity(Number.isFinite(next) && next >= 1 ? Math.floor(next) : 1);
            }} className="h-11 w-12 border-x border-ink/25 bg-white text-center text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-inset focus:ring-pegasus-blue/40" data-id="element-326" data-mp="10g4umjspp6f3" />
              <button type="button" aria-label={`Increase quantity of ${product.name}`} onClick={() => setQuantity(value => value + 1)} className="flex h-11 w-11 items-center justify-center text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-327" data-mp="1t38wwh0f76n6">
                <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-328" data-mp="1y1tx4gfo9qbm" />
              </button>
            </div>
          </div>}

          <button type="button" disabled={isOutOfStock} aria-disabled={isOutOfStock || undefined} onClick={() => onAddToCart(quantity)} className={`mt-3 inline-flex min-h-[48px] w-full items-center justify-center rounded-btn px-4 text-[16px] font-bold transition-colors duration-150 ease-out ${isOutOfStock ? 'cursor-not-allowed border border-ink/20 bg-ink/5 text-ink/55' : 'bg-pegasus-magenta text-white hover:bg-[#bb2f83]'}`} data-id="element-329" data-mp="faxa0t4krdb">
            {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
          </button>

          <SiteLink to={productDetailRoute} className="mt-2 inline-flex min-h-[44px] items-center text-[15px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-330" data-mp="3molwjy8bjbjc">
            View Details
          </SiteLink>
        </div>
      </div>
    </article>;
}